import clsx from 'clsx';
import { useNavigate } from 'react-router';
import { IconButton, LogoMark } from '@/design-system';
import { ns, useT } from '@/i18n';
import { paths } from '../paths';
import { SearchPill } from './SearchPill';
import type { ShellHandle } from './shellConfig';
import { useShellOverrides } from './ShellContext';
import { UniverseSwitch } from './UniverseSwitch';
import { useBack } from './useBack';
import { useUnreadCount } from './useUnread';
import type { ScrollState } from './useScrollState';
import styles from './MobileTopBar.module.css';

/** Fixed 64 px glass top bar (`.hm`), ≤ 767 px. */
export function MobileTopBar({ handle, scroll }: { handle: ShellHandle; scroll: ScrollState }) {
  const navigate = useNavigate();
  const t = useT(ns.shell);
  const tc = useT(ns.common);
  const back = useBack(handle.parent);
  const overrides = useShellOverrides();
  const unread = useUnreadCount();
  const over = !!handle.hero && !scroll.scrolled;
  const top = handle.top;

  if (top.kind === 'root') {
    return (
      <header className={clsx(styles.hm, over && styles.over)}>
        <button
          type="button"
          className={styles.logo}
          onClick={() => navigate(paths.home)}
          aria-label={tc('brand.home')}
        >
          <LogoMark size={36} inverted={over} />
        </button>
        {top.switch ? (
          <div className={styles.switch}>
            <UniverseSwitch universe={handle.universe} compact overHero={over} />
          </div>
        ) : (
          <span className={styles.title}>{overrides.title ?? (top.title ? t(top.title) : '')}</span>
        )}
        {overrides.actions ?? (
          <IconButton
            icon="chat"
            size={42}
            label={t('messages.unread', { count: unread })}
            badge={unread}
            overHero={over}
            onClick={() => navigate(paths.messages)}
          />
        )}
      </header>
    );
  }

  const title = overrides.title ?? (top.title ? t(top.title) : '');
  return (
    <header className={clsx(styles.hm, over && styles.over)}>
      <IconButton icon="chevronLeft" size={42} label={tc('back')} iconSize={20} strokeWidth={2.2} onClick={back} />
      {top.pill ? <SearchPill mobile /> : <span className={clsx(styles.title, styles.centered)}>{title}</span>}
      {overrides.actions ?? <span className={styles.spacer} aria-hidden="true" />}
    </header>
  );
}
